// The Pricing dialog: what KilatDomain pays the registry for every term of one TLD, as a grid of
// 1 to 10 years × register, transfer and renewal in rupiah (money-input.tsx), each term with its
// Enable box. An empty price means the action is not offered for that term. Superadmin and finance
// edit and save the whole grid at once — checked against the contract first, the bad cells
// marked — and confirm with a fresh
// authenticator code when the API asks (step-up.tsx); anyone else reads it. Closing the dialog
// unmounts the form, so it opens on what is saved.
import { SetTldCostsRequest, type TldPricing } from "@kilat/contracts/tld-pricing";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FieldGroup } from "@/components/ui/field";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { m } from "@/lib/paraglide/messages.js";
import { pricingError } from "../pricing-error";
import { needsStepUp, StepUpPrompt } from "../step-up";
import { costRows, PRICE_FIELDS, toCosts, type CostRow, type PriceField } from "./cost-input";
import { MoneyInput } from "@/features/pricing/money-input";
import { useSetTldCosts } from "./mutation";

type Props = { tld: TldPricing; editable: boolean };

const ACTION_LABELS: Record<PriceField, () => string> = {
  register: m.tld_pricing_column_register,
  transfer: m.tld_pricing_column_transfer,
  renew: m.tld_pricing_column_renew,
};

/** "1:register" for each cell the contract refused. */
const cellKey = (index: number, field: string) => `${index}:${field}`;

function CostsForm({ tld, editable, onClose }: Props & { onClose: () => void }) {
  const [rows, setRows] = useState<CostRow[]>(() => costRows(tld.costs));
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState<SetTldCostsRequest | null>(null);
  const save = useSetTldCosts(tld.id);
  const name = `.${tld.tld}`;

  const change = (index: number, patch: Partial<CostRow>) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  function send(costs: SetTldCostsRequest) {
    setPending(costs);
    save.mutate(costs, {
      onSuccess: () => {
        toast.success(m.tld_pricing_costs_saved({ tld: name }));
        onClose();
      },
    });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = SetTldCostsRequest.safeParse({ costs: toCosts(rows) });
    if (parsed.success) {
      setInvalid(new Set());
      send(parsed.data);
      return;
    }
    // Issues name the cell as ["costs", index, field].
    setInvalid(new Set(parsed.error.issues.map(({ path }) => cellKey(Number(path[1]), String(path[2])))));
  }

  if (needsStepUp(save.error) && pending) {
    return <StepUpPrompt onConfirmed={() => send(pending)} onCancel={onClose} />;
  }

  const error = pricingError(save.error);
  return (
    <form onSubmit={submit} noValidate>
      <FieldGroup>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{m.tld_pricing_column_term()}</TableHead>
              <TableHead>{m.tld_pricing_column_enabled()}</TableHead>
              {PRICE_FIELDS.map((field) => (
                <TableHead key={field}>{ACTION_LABELS[field]()}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.years}>
                <TableCell>{m.tld_pricing_costs_years({ years: row.years })}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={row.enabled}
                    disabled={!editable}
                    aria-label={m.tld_pricing_enabled_label({ years: row.years })}
                    onCheckedChange={(enabled) => change(index, { enabled })}
                  />
                </TableCell>
                {PRICE_FIELDS.map((field) => (
                  <TableCell key={field}>
                    <MoneyInput
                      className="min-w-28"
                      disabled={!editable}
                      aria-label={m.tld_pricing_cost_label({ action: ACTION_LABELS[field](), years: row.years })}
                      aria-invalid={invalid.has(cellKey(index, field))}
                      value={row[field]}
                      onChange={(amount) => change(index, { [field]: amount })}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {invalid.size > 0 && (
          <Alert variant="destructive">
            <AlertDescription>{m.tld_pricing_costs_invalid()}</AlertDescription>
          </Alert>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {m.tld_pricing_close()}
          </Button>
          {editable && (
            <Button type="submit" disabled={save.isPending}>
              {m.tld_pricing_costs_save()}
            </Button>
          )}
        </DialogFooter>
      </FieldGroup>
    </form>
  );
}

export function SetTldCostDialog({ tld, editable }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="outline" size="sm" />}>{m.tld_pricing_open()}</DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{m.tld_pricing_costs_title({ tld: `.${tld.tld}` })}</DialogTitle>
          <DialogDescription>{m.tld_pricing_costs_description()}</DialogDescription>
        </DialogHeader>
        <CostsForm tld={tld} editable={editable} onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}
